import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, MapPin } from "lucide-react";

import { serverMessage } from "@/lib/apiError";
import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getMyCoverageAreas,
  replaceMyCoverageAreas,
} from "@/services/professionalCoverageService";
import { CoverageAreaPicker } from "@/components/shared/coverage-area-picker";
import { departmentLabel } from "@/lib/department-label";

const QUERY_KEY = ["me", "coverage-areas"] as const;

/**
 * The departments a professional covers (spec 8ec68023 D1).
 *
 * Several, because many genuinely cover several — someone in Ciudad de la
 * Costa may take work in both Montevideo and Canelones.
 *
 * WHAT THIS DOES AND DOES NOT DO, stated on the card itself: jobs in a covered
 * department sort to the top of their list and are marked. It does NOT limit
 * what they are shown or notified about. Leaving that unsaid invites the
 * opposite reading — that unticking a department hides work — which would make
 * a professional under-claim out of caution and quietly cost them jobs.
 *
 * WHY IT IS ON THE PROFILE PAGE ITSELF. It used to live at the bottom of
 * /profile/edit, below the name-and-photo form, which is a screen you reach by
 * pressing "Editar" on your personal details — so a professional looking for
 * their coverage found a page about their name. That is the same
 * discoverability failure that made this data empty for everyone in the first
 * place (0 of 8 professionals had ever declared one, task 4228162).
 *
 * BUILT OUT OF THE SAME PIECES AS MyFiscalDeclarationCard, deliberately — same
 * CardHeader/CardTitle/CardDescription, same DetailRows, same StatusBadge,
 * same right-aligned outline "Editar". The first version of this card invented
 * its own layout (an icon-indented h2 with the explanation as body copy) and
 * sat directly below the fiscal card looking like it came from a different
 * product: its heading started at a different x than its own text. These two
 * cards answer the same shape of question — a professional-only declaration
 * the server reads on their behalf — so they are built the same way rather
 * than similarly.
 *
 * VIEW FIRST, EDIT ON REQUEST, for the reason the sibling card states:
 * rendering nineteen checkboxes unconditionally would make a settled
 * declaration look unsettled every time someone opened their profile. The
 * summary answers "is this right?" and the button answers "change it".
 */
export function CoverageAreasCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  // Seeded once. A window-focus refetch would otherwise discard whatever the
  // professional is halfway through ticking.
  const seeded = useRef(false);

  const coverageQuery = useQuery({ queryKey: QUERY_KEY, queryFn: getMyCoverageAreas });

  useEffect(() => {
    if (!coverageQuery.data || seeded.current) return;
    seeded.current = true;
    setSelected(coverageQuery.data.departments);
  }, [coverageQuery.data]);

  const available = coverageQuery.data?.available ?? [];
  const stored = useMemo(
    () => coverageQuery.data?.departments ?? [],
    [coverageQuery.data],
  );

  // Compared as SETS, not as arrays: the server returns them sorted and the
  // checkbox order is the vocabulary's, so an array compare would offer a save
  // that changes nothing.
  const isDirty = useMemo(() => {
    if (selected.length !== stored.length) return true;
    const storedSet = new Set(stored);
    return selected.some((d) => !storedSet.has(d));
  }, [selected, stored]);

  const saveMutation = useMutation({
    mutationFn: replaceMyCoverageAreas,
    onSuccess: async (saved) => {
      toast.success(t("profile.coverage.saveSuccess"));
      setSelected(saved.departments);
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: unknown) =>
      toast.error(serverMessage(err) ?? t("profile.coverage.saveError")),
  });

  /*
    SAYS "the whole country" RATHER THAN LISTING NINETEEN NAMES — but only when
    the stored set genuinely covers the served vocabulary, so it stays a
    readback of what was declared and never a flattering summary of something
    narrower.
  */
  const coversEverywhere =
    available.length > 0 && available.every((slug) => stored.includes(slug));

  const declared = stored.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {t("profile.coverage.title")}
        </CardTitle>
        <CardDescription>{t("profile.coverage.description")}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {coverageQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : coverageQuery.isError ? (
          <p className="text-sm text-muted-foreground">{t("profile.coverage.loadError")}</p>
        ) : editing ? (
          <div className="space-y-4">
            <CoverageAreaPicker
              available={available}
              selected={selected}
              onChange={setSelected}
              disabled={saveMutation.isPending}
            />

            {/* Says what an empty selection MEANS rather than blocking it.
                Covering nothing is a legitimate answer and shows every request
                unranked (control C-4); treating it as an error would push
                professionals into ticking departments they do not serve. */}
            {selected.length === 0 ? (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t("profile.coverage.noneSelected")}
              </p>
            ) : null}

            {/* Right-aligned per rule 1a8a68a0. */}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                disabled={saveMutation.isPending}
                onClick={() => {
                  // Back to what is STORED, not to whatever was ticked before
                  // opening the editor — cancel has to mean "forget this".
                  setSelected(stored);
                  setEditing(false);
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!isDirty || saveMutation.isPending}
                onClick={() => saveMutation.mutate(selected)}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                ) : null}
                {t("profile.coverage.save")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <DetailRows>
              <DetailRow label={t("profile.coverage.status")}>
                <StatusBadge variant={declared ? "success" : "warning"}>
                  {t(
                    declared
                      ? "profile.coverage.status_declared"
                      : "profile.coverage.status_notDeclared",
                  )}
                </StatusBadge>
              </DetailRow>
              <DetailRow label={t("profile.coverage.departments")}>
                {!declared
                  ? "—"
                  : coversEverywhere
                    ? t("profile.coverage.declaredAll")
                    : stored.map(departmentLabel).join(" · ")}
              </DetailRow>
            </DetailRows>

            {/* Only when there is nothing declared. The reassurance that
                coverage does not LIMIT anything is what stops a professional
                under-claiming out of caution, but it is noise once they have
                answered — the same reason the fiscal card shows its hint only
                while the declaration is incomplete. */}
            {!declared ? (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t("profile.coverage.noneSelected")}
              </p>
            ) : null}

            {/* Right-aligned per rule 1a8a68a0. */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t("profile.coverage.edit")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
